/**
 * /app: where a firm sends its clients to install "Your Case" on Android.
 *
 * Public, like the landing and demo pages. The button goes to the API, which redirects
 * to a ten-minute link to the current build, so this page never holds a link that can
 * go stale. Installing is a sideload until the app is on Google Play, so the steps for
 * allowing it are spelled out rather than left to the person to search for.
 */

import { androidRelease, type AndroidRelease, EXACT_PATHS } from '@legalcrm/api';
import { Card, CardBody, Spinner } from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiClient } from '@/api/client';

const STEPS = [
  'Tap Download on your Android phone.',
  'Open the downloaded file. If your phone asks, allow installing apps from your browser or Files app — only for this install.',
  'Open Your Case and sign in with the email and password your law firm gave you.',
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function AppDownloadPage() {
  const [release, setRelease] = useState<AndroidRelease | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let current = true;
    androidRelease(apiClient).then(
      (found) => current && setRelease(found),
      () => current && setFailed(true),
    );
    return () => {
      current = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-hero px-4 py-10 text-hero-ink sm:py-16">
      <div className="mx-auto grid max-w-5xl items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Link to="/" className="inline-flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-hero-ink font-display text-lg font-semibold text-hero">
              W
            </span>
            <span className="leading-tight">
              <span className="block text-sm font-semibold">WPCodie</span>
              <span className="block text-xs text-hero-muted">Legal CRM</span>
            </span>
          </Link>
          <h1 className="mt-10 font-display text-4xl leading-tight sm:text-5xl">
            Your Case, on your phone.
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-hero-muted">
            Follow your matter step by step, send the documents your lawyer asks for, sign
            agreements and message your legal team, all from one app.
          </p>
        </div>

        <Card className="text-ink">
          <CardBody>
            <h2 className="font-display text-2xl">Download for Android</h2>

            {!release && !failed && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}

            {(failed || release?.available === false) && (
              <p role="status" className="mt-3 text-sm leading-relaxed text-ink-muted">
                The Android app is not available to download yet. Please check back soon, or
                ask your law firm.
              </p>
            )}

            {release?.available && (
              <>
                <p className="mt-2 text-sm text-ink-muted">
                  Version {release.version} &middot; {formatDate(release.published_at)} &middot;{' '}
                  {(release.size_bytes / 1_000_000).toFixed(0)} MB
                </p>
                <a
                  href={EXACT_PATHS.appAndroidDownload}
                  className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded bg-primary px-6 text-lg font-medium text-primary-ink shadow-sm transition-colors hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  Download
                </a>
                <ol className="mt-6 space-y-3 text-sm leading-relaxed text-ink-muted">
                  {STEPS.map((step, index) => (
                    <li key={step} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-ink">
                        {index + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
                <p className="mt-6 text-xs text-ink-subtle">
                  On an iPhone? The app is not in the App Store yet; your law firm can give you
                  access to the same information in your browser.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
